import { memo } from 'react';
import { TrendingUp, Eye, Award, Target, BarChart3, MousePointer } from 'lucide-react';

export const AEO = memo(({ data, loading, error }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <p className="font-semibold">Error loading AEO data</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-gray-600">
        No data available
      </div>
    );
  }

  const { metrics, changes, aiEngines } = data;

  const MetricCard = ({ label, value, change, icon: Icon, suffix = '' }) => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {typeof value === 'number' && value >= 1000 
              ? value.toLocaleString() 
              : value}{suffix}
          </p>
          {change !== null && change !== undefined && (
            <div className="mt-2 flex items-center text-sm">
              <span className={`font-semibold ${
                // For position, lower is better (negative change is good)
                label.includes('Position') 
                  ? (change < 0 ? 'text-green-600' : 'text-red-600')
                  : (change > 0 ? 'text-green-600' : 'text-red-600')
              }`}>
                {label.includes('Position')
                  ? (change < 0 ? '↓' : '↑')
                  : (change > 0 ? '↑' : '↓')} {Math.abs(change).toFixed(1)}%
              </span>
              <span className="text-gray-500 ml-1">vs last period</span>
            </div>
          )}
        </div>
        {Icon && (
          <div className="p-3 bg-blue-50 rounded-lg">
            <Icon className="w-6 h-6 text-blue-600" />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <MetricCard
          label="Referral Traffic"
          value={metrics.referralTraffic}
          change={changes.referralTraffic}
          icon={TrendingUp}
        />
        <MetricCard
          label="AEO Impressions"
          value={metrics.aeoImpressions}
          change={changes.aeoImpressions}
          icon={Eye}
        />
        <MetricCard
          label="Citation Frequency"
          value={metrics.citationFrequency}
          change={changes.citationFrequency}
          icon={Award}
        />
        <MetricCard
          label="AI Engine Visibility"
          value={metrics.aiEngineVisibility}
          change={changes.aiEngineVisibility}
          icon={Target}
          suffix="%"
        />
        <MetricCard
          label="Average Position"
          value={metrics.averagePosition}
          change={changes.averagePosition}
          icon={BarChart3}
        />
        <MetricCard
          label="Click-Through Rate"
          value={metrics.clickThroughRate}
          change={changes.clickThroughRate}
          icon={MousePointer}
          suffix="%"
        />
      </div>

      {/* AI Engines Breakdown */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h4 className="font-semibold text-gray-900">AI Engine Breakdown</h4>
          <p className="text-sm text-gray-600 mt-1">Performance across different AI platforms</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                  AI Engine
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                  Citations
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                  Referrals
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                  Impressions
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                  Conversion Rate
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {aiEngines.map((engine, index) => {
                const conversionRate = (engine.referrals / engine.impressions) * 100;
                
                return (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-900">{engine.name}</p>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <p className="text-sm font-semibold text-gray-900">{engine.citations}</p>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <p className="text-sm font-semibold text-green-700">
                        {engine.referrals.toLocaleString()}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <p className="text-sm font-semibold text-gray-900">
                        {engine.impressions.toLocaleString()}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        conversionRate > 4 
                          ? 'bg-green-100 text-green-800'
                          : conversionRate > 3
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {conversionRate.toFixed(2)}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 border-t-2 border-gray-300">
              <tr>
                <td className="px-6 py-4 text-sm font-bold text-gray-900">
                  Total
                </td>
                <td className="px-6 py-4 text-center text-sm font-bold text-gray-900">
                  {aiEngines.reduce((sum, e) => sum + e.citations, 0)}
                </td>
                <td className="px-6 py-4 text-center text-sm font-bold text-green-700">
                  {aiEngines.reduce((sum, e) => sum + e.referrals, 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 text-center text-sm font-bold text-gray-900">
                  {aiEngines.reduce((sum, e) => sum + e.impressions, 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 text-center text-sm font-bold text-gray-900">
                  {((aiEngines.reduce((sum, e) => sum + e.referrals, 0) / 
                     aiEngines.reduce((sum, e) => sum + e.impressions, 0)) * 100).toFixed(2)}%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Insights */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-lg border border-blue-200">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Award className="w-5 h-5 text-blue-600" />
          AEO Performance Insights
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-700">
              <span className="font-semibold">Top Performer:</span>{' '}
              {aiEngines.reduce((max, e) => e.citations > max.citations ? e : max).name} with{' '}
              {aiEngines.reduce((max, e) => e.citations > max.citations ? e : max).citations} citations
            </p>
          </div>
          <div>
            <p className="text-gray-700">
              <span className="font-semibold">Most Traffic:</span>{' '}
              {aiEngines.reduce((max, e) => e.referrals > max.referrals ? e : max).name} driving{' '}
              {aiEngines.reduce((max, e) => e.referrals > max.referrals ? e : max).referrals.toLocaleString()} referrals
            </p>
          </div>
          <div>
            <p className="text-gray-700">
              <span className="font-semibold">Best Conversion:</span>{' '}
              {aiEngines.reduce((max, e) => {
                const rate = (e.referrals / e.impressions);
                return rate > (max.referrals / max.impressions) ? e : max;
              }).name} at {((aiEngines.reduce((max, e) => {
                const rate = (e.referrals / e.impressions);
                return rate > (max.referrals / max.impressions) ? e : max;
              }).referrals / aiEngines.reduce((max, e) => {
                const rate = (e.referrals / e.impressions);
                return rate > (max.referrals / max.impressions) ? e : max;
              }).impressions) * 100).toFixed(2)}%
            </p>
          </div>
          <div>
            <p className="text-gray-700">
              <span className="font-semibold">Total Reach:</span>{' '}
              {metrics.aeoImpressions.toLocaleString()} impressions across all AI engines
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

AEO.displayName = 'AEO';